import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { 
  Flame, TrendingUp, Target, Award, BookOpen, 
  Clock, Zap, Trophy, Brain, ChevronRight, Star,
  AlertCircle, LogOut, User, Gamepad2, Sparkles,
  FileText, Calendar, Heart, Library, Camera, School, Video,
  Microscope, Languages
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AvatarSVG } from '../components/AvatarBuilder';
import { motion } from 'framer-motion';

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subStatus, setSubStatus] = useState(null);
  const [classChallenges, setClassChallenges] = useState([]);

  useEffect(() => {
    // Redirect non-students to their respective dashboards
    if (user?.role === 'admin') {
      navigate('/admin', { replace: true });
      return;
    }
    if (user?.role === 'teacher') {
      navigate('/teacher', { replace: true });
      return;
    }
    if (user?.role === 'parent') {
      navigate('/parent', { replace: true });
      return;
    }
    fetchDashboard();
  }, [user, navigate]);

  const fetchDashboard = async () => {
    try {
      const response = await apiService.getDashboard();
      setDashboardData(response.data);
      // Also fetch subscription status for trial banner
      try {
        const s = await apiService.subscriptionStatus();
        setSubStatus(s.data);
      } catch (e) { /* ignore */ }
      // Class challenges (only if school student)
      if (user?.class_id) {
        try {
          const c = await apiService.activeChallenges();
          setClassChallenges(c.data.challenges || []);
        } catch (e) { /* ignore */ }
      }
    } catch (error) {
      toast.error('Failed to load dashboard');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F4F8FB] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#4D96FF] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-lg font-semibold text-[#64748B]">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  const currentUser = dashboardData?.student || user;
  const accuracy = dashboardData?.accuracy || 0;
  const subjectStats = dashboardData?.subject_stats || [];
  const weakAreas = dashboardData?.weak_areas || [];

  return (
    <div className="min-h-screen bg-[#F4F8FB]" data-testid="dashboard-page">
      {/* Top Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b-2 border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center gap-4">
              <img src="/logo.png" alt="SkillRise Logo" className="w-12 h-12 rounded-2xl object-cover shadow-lg" />
              <div>
                <h1 className="text-2xl font-black tracking-tight text-[#1E293B]">SkillRise</h1>
                <p className="text-[10px] text-[#64748B] font-semibold tracking-widest uppercase">by Ekamraj Productions</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              {/* Streak Display */}
              <div data-testid="streak-display" className="flex items-center gap-2 bg-gradient-to-r from-orange-400 to-red-500 text-white px-5 py-2.5 rounded-2xl border-2 border-white shadow-lg streak-glow">
                <Flame className="w-6 h-6" />
                <div>
                  <p className="text-xs font-semibold opacity-90">Day Streak</p>
                  <p className="text-xl font-black">{currentUser?.current_streak || 0}</p>
                </div>
              </div>

              {/* Level Badge */}
              <div data-testid="level-badge" className="flex items-center gap-2 bg-[#FFC03D] text-white px-5 py-2.5 rounded-2xl border-2 border-white shadow-lg">
                <Trophy className="w-6 h-6" />
                <div>
                  <p className="text-xs font-semibold opacity-90">Level</p>
                  <p className="text-xl font-black">{currentUser?.level || 1}</p>
                </div>
              </div>

              {/* Profile Menu */}
              <div className="relative">
                <button onClick={() => navigate('/profile')} data-testid="profile-btn"
                  className="hover:scale-105 transition-all">
                  <AvatarSVG skin={currentUser?.avatar_skin || '#FFD5A0'} hair={currentUser?.avatar_hair || '#4A3728'} eyes={currentUser?.avatar_eyes || '#4D96FF'} accessory={currentUser?.avatar_accessory || 'none'} size={48} />
                </button>
              </div>

              <button
                onClick={handleLogout}
                data-testid="logout-btn"
                className="px-4 py-2 text-[#64748B] hover:text-[#1E293B] font-semibold rounded-2xl hover:bg-slate-100 transition-all"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Trial Banner */}
        {subStatus?.in_trial && subStatus.trial_days_left > 0 && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} data-testid="trial-banner"
            className="bg-gradient-to-r from-amber-500 to-orange-600 rounded-2xl p-4 mb-6 text-white flex items-center justify-between flex-wrap gap-3 shadow-md">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <Sparkles className="w-5 h-5" />
              </div>
              <div>
                <p className="font-black text-base">Free Trial Active</p>
                <p className="text-white/90 text-sm">{subStatus.trial_days_left} {subStatus.trial_days_left === 1 ? 'day' : 'days'} remaining. After this, ₹1500/month to keep your data and full access.</p>
              </div>
            </div>
            <button onClick={() => navigate('/paywall')} data-testid="trial-upgrade-btn"
              className="px-5 py-2.5 bg-white text-orange-700 rounded-xl font-bold text-sm hover:scale-105 transition shadow">
              Subscribe Now
            </button>
          </motion.div>
        )}
        
        {/* Animated Welcome Banner */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="bg-gradient-to-r from-[#4D96FF] via-[#6C63FF] to-[#A493FF] rounded-3xl p-6 mb-8 text-white relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full"></div>
          <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-white/5 rounded-full"></div>
          <div className="flex items-center justify-between relative z-10">
            <div>
              <motion.p initial={{ x: -20 }} animate={{ x: 0 }} className="text-white/80 font-semibold">Welcome back,</motion.p>
              <motion.h2 initial={{ x: -20 }} animate={{ x: 0 }} transition={{ delay: 0.1 }}
                className="text-3xl font-black">{currentUser?.username}! 👋</motion.h2>
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
                className="text-white/80 mt-1">
                {currentUser?.current_streak > 0 
                  ? `You're on a ${currentUser.current_streak}-day streak! Keep it going!`
                  : "Start practicing to build your streak!"}
              </motion.p>
            </div>
            <motion.div animate={{ y: [0, -8, 0], rotate: [0, 5, -5, 0] }} transition={{ repeat: Infinity, duration: 3 }} className="hidden md:block">
              <AvatarSVG skin={currentUser?.avatar_skin || '#FFD5A0'} hair={currentUser?.avatar_hair || '#4A3728'} eyes={currentUser?.avatar_eyes || '#4D96FF'} accessory={currentUser?.avatar_accessory || 'none'} size={80} />
            </motion.div>
          </div>
        </motion.div>

        {/* Growth Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            icon={<TrendingUp className="w-7 h-7" />}
            label="Accuracy"
            value={`${accuracy.toFixed(1)}%`}
            bgColor="bg-[#D8E8FF]"
            iconColor="text-[#4D96FF]"
            trend="+15%"
          />
          <StatCard
            icon={<Target className="w-7 h-7" />}
            label="Questions Answered"
            value={currentUser?.total_questions_answered || 0}
            bgColor="bg-[#FFF3D6]"
            iconColor="text-[#FFC03D]"
          />
          <StatCard
            icon={<Zap className="w-7 h-7" />}
            label="Elo Rating"
            value={Math.round(currentUser?.overall_elo_rating || 1500)}
            bgColor="bg-[#F3EBFF]"
            iconColor="text-[#A493FF]"
          />
          <StatCard
            icon={<Award className="w-7 h-7" />}
            label="Badges Earned"
            value={currentUser?.badges?.length || 0}
            bgColor="bg-[#E8F5E9]"
            iconColor="text-[#38E54D]"
          />
        </div>

        {/* Weak Areas Alert */}
        {weakAreas.length > 0 && (
          <div data-testid="weak-areas-alert" className="mb-8 bg-gradient-to-r from-orange-100 to-yellow-100 border-2 border-orange-300 rounded-3xl p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-orange-400 rounded-2xl flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-[#1E293B] mb-2">Areas that need a little boost! 💪</h3>
                <div className="space-y-2">
                  {weakAreas.map((area, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                      <p className="text-[#64748B] font-medium">
                        <span className="capitalize font-bold text-[#1E293B]">{area.subject}</span> - 
                        {" "}{((area.correct_answers / area.questions_answered) * 100).toFixed(0)}% accuracy
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <ActionCard
            title="Practice Math"
            description="Sharpen your problem-solving skills"
            icon={<Brain className="w-10 h-10" />}
            bgGradient="from-blue-400 to-blue-600"
            onClick={() => navigate('/practice/mathematics')}
            testId="practice-math-btn"
          />
          <ActionCard
            title="Practice Science"
            description="Explore scientific concepts"
            icon={<BookOpen className="w-10 h-10" />}
            bgGradient="from-green-400 to-green-600"
            onClick={() => navigate('/practice/science')}
            testId="practice-science-btn"
          />
          <ActionCard
            title="Practice English"
            description="Improve your language skills"
            icon={<BookOpen className="w-10 h-10" />}
            bgGradient="from-purple-400 to-purple-600"
            onClick={() => navigate('/practice/english')}
            testId="practice-english-btn"
          />
          <ActionCard
            title="Brain Arena"
            description="15 fun interactive games!"
            icon={<Zap className="w-10 h-10" />}
            bgGradient="from-[#FF6B6B] via-[#FFC03D] to-[#4D96FF]"
            onClick={() => navigate('/games')}
            testId="brain-arena-btn"
          />
        </div>

        {/* Study Hub Quick Access */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-10">
          <div onClick={() => navigate('/study-hub')} data-testid="study-hub-btn"
            className="bg-white rounded-2xl p-6 border border-slate-200 cursor-pointer hover:-translate-y-1 hover:shadow-xl hover:border-blue-300 transition-all">
            <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center mb-3"><FileText className="w-5 h-5 text-blue-600" /></div>
            <p className="font-bold text-[#1E293B] text-sm">AI Worksheets</p>
            <p className="text-xs text-[#64748B] mt-1">Personalised practice</p>
          </div>
          <div onClick={() => { navigate('/study-hub'); setTimeout(() => document.querySelector('[data-testid="tab-timetable"]')?.click(), 100); }} data-testid="timetable-btn"
            className="bg-white rounded-2xl p-6 border border-slate-200 cursor-pointer hover:-translate-y-1 hover:shadow-xl hover:border-green-300 transition-all">
            <div className="w-11 h-11 rounded-xl bg-green-50 flex items-center justify-center mb-3"><Clock className="w-5 h-5 text-green-600" /></div>
            <p className="font-bold text-[#1E293B] text-sm">My Timetable</p>
            <p className="text-xs text-[#64748B] mt-1">No-pressure plan</p>
          </div>
          <div onClick={() => { navigate('/study-hub'); setTimeout(() => document.querySelector('[data-testid="tab-tests"]')?.click(), 100); }} data-testid="upcoming-tests-btn"
            className="bg-white rounded-2xl p-6 border border-slate-200 cursor-pointer hover:-translate-y-1 hover:shadow-xl hover:border-amber-300 transition-all">
            <div className="w-11 h-11 rounded-xl bg-amber-50 flex items-center justify-center mb-3"><Calendar className="w-5 h-5 text-amber-600" /></div>
            <p className="font-bold text-[#1E293B] text-sm">Test Alerts</p>
            <p className="text-xs text-[#64748B] mt-1">Track your exams</p>
          </div>
          <div onClick={() => { navigate('/study-hub'); setTimeout(() => document.querySelector('[data-testid="tab-emotional"]')?.click(), 100); }} data-testid="emotional-btn"
            className="bg-white rounded-2xl p-6 border border-slate-200 cursor-pointer hover:-translate-y-1 hover:shadow-xl hover:border-pink-300 transition-all">
            <div className="w-11 h-11 rounded-xl bg-pink-50 flex items-center justify-center mb-3"><Heart className="w-5 h-5 text-pink-600" /></div>
            <p className="font-bold text-[#1E293B] text-sm">How I Feel</p>
            <p className="text-xs text-[#64748B] mt-1">Emotional check-in</p>
          </div>
        </motion.div>

        {/* Books / Homework Scanner / Competitions / My Class */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
          className={`grid grid-cols-1 sm:grid-cols-2 ${user?.class_id ? 'lg:grid-cols-5' : 'lg:grid-cols-4'} gap-5 mb-10`}>
          <div onClick={() => navigate('/books')} data-testid="books-library-btn"
            className="bg-white rounded-2xl p-6 border border-slate-200 cursor-pointer hover:-translate-y-1 hover:shadow-xl hover:border-amber-300 transition-all">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center mb-4"><Library className="w-6 h-6 text-white" /></div>
            <p className="font-black text-lg text-[#1E293B]">Books Library</p>
            <p className="text-sm text-[#64748B] mt-1">Upload PDFs/EPUBs. Summaries, Q&A, quizzes.</p>
          </div>
          <div onClick={() => navigate('/homework')} data-testid="homework-scanner-btn"
            className="bg-white rounded-2xl p-6 border border-slate-200 cursor-pointer hover:-translate-y-1 hover:shadow-xl hover:border-cyan-300 transition-all">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-600 to-blue-700 flex items-center justify-center mb-4"><Camera className="w-6 h-6 text-white" /></div>
            <p className="font-black text-lg text-[#1E293B]">Homework Scanner</p>
            <p className="text-sm text-[#64748B] mt-1">Scan questions or your solutions for AI grading.</p>
          </div>
          <div onClick={() => navigate('/competitions')} data-testid="competitions-btn"
            className="bg-white rounded-2xl p-6 border border-slate-200 cursor-pointer hover:-translate-y-1 hover:shadow-xl hover:border-yellow-300 transition-all">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-600 to-yellow-700 flex items-center justify-center mb-4"><Trophy className="w-6 h-6 text-white" /></div>
            <p className="font-black text-lg text-[#1E293B]">Competitions</p>
            <p className="text-sm text-[#64748B] mt-1">Upload certificates and achievements.</p>
          </div>
          <div onClick={() => navigate('/videos')} data-testid="video-lab-btn"
            className="bg-white rounded-2xl p-6 border border-slate-200 cursor-pointer hover:-translate-y-1 hover:shadow-xl hover:border-violet-300 transition-all">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-600 to-fuchsia-700 flex items-center justify-center mb-4"><Video className="w-6 h-6 text-white" /></div>
            <p className="font-black text-lg text-[#1E293B]">AI Video Lab</p>
            <p className="text-sm text-[#64748B] mt-1">Generate concept explainer videos with Sora 2.</p>
          </div>
          {user?.class_id && (
            <div onClick={() => navigate('/profile')} data-testid="join-class-btn"
              className="bg-white rounded-2xl p-6 border border-slate-200 cursor-pointer hover:-translate-y-1 hover:shadow-xl hover:border-indigo-300 transition-all">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-600 to-purple-700 flex items-center justify-center mb-4"><School className="w-6 h-6 text-white" /></div>
              <p className="font-black text-lg text-[#1E293B]">My Class</p>
              <p className="text-sm text-[#64748B] mt-1">Enrolled in {user.class_name}</p>
            </div>
          )}
        </motion.div>

        {/* Class Challenges widget (school students only) */}
        {user?.class_id && classChallenges.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
            className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-6 border-2 border-amber-200 mb-10" data-testid="class-challenges-widget">
            <h2 className="text-xl font-black text-[#1E293B] mb-3 flex items-center gap-2">
              <Trophy className="w-6 h-6 text-amber-500" />Class Challenges
            </h2>
            <div className="space-y-4">
              {classChallenges.map(ch => {
                const me = (ch.leaderboard || []).find(p => p.id === user.id);
                const myRank = me ? (ch.leaderboard || []).findIndex(p => p.id === user.id) + 1 : null;
                return (
                  <div key={ch.id} className="bg-white rounded-2xl p-4 border border-amber-100">
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-black text-[#1E293B]">{ch.title}</p>
                      <span className="text-[10px] font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full uppercase">{ch.target_type.replace(/_/g, ' ')}</span>
                    </div>
                    {me && (
                      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-3 mb-3">
                        <p className="text-xs font-bold text-[#64748B] uppercase mb-1">Your progress · Rank #{myRank}</p>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-slate-200 rounded-full h-2.5"><div className={`h-2.5 rounded-full ${me.completed ? 'bg-green-500' : 'bg-indigo-500'}`} style={{ width: `${Math.min(100, me.progress / ch.target_value * 100)}%` }}></div></div>
                          <span className="font-mono text-xs font-bold text-[#1E293B]">{me.progress}/{ch.target_value}</span>
                          {me.completed && <Award className="w-4 h-4 text-green-600" />}
                        </div>
                      </div>
                    )}
                    <p className="text-[10px] font-bold text-[#64748B] uppercase mb-1">Class leaderboard</p>
                    <div className="space-y-1">
                      {(ch.leaderboard || []).slice(0, 5).map((p, i) => (
                        <div key={p.id} className={`flex items-center gap-2 p-1.5 rounded-lg ${p.id === user.id ? 'bg-indigo-50 ring-1 ring-indigo-200' : ''}`}>
                          <span className={`w-6 h-6 rounded-full text-white text-xs font-black flex items-center justify-center ${i === 0 ? 'bg-amber-500' : i === 1 ? 'bg-slate-400' : i === 2 ? 'bg-orange-700' : 'bg-slate-300'}`}>{i + 1}</span>
                          <span className="font-bold text-sm text-[#1E293B] flex-1 truncate">{p.username}{p.id === user.id ? ' (you)' : ''}</span>
                          <span className="font-mono text-xs font-bold text-indigo-600">{p.progress}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Subject Performance */}
        <div className="bg-white rounded-3xl p-6 sm:p-10 border border-slate-200 shadow-sm mb-10">
          <h2 className="text-2xl sm:text-3xl font-black text-[#1E293B] mb-2 flex items-center gap-3">
            <BookOpen className="w-7 h-7 text-[#4D96FF]" />
            Chapter-wise Learning
          </h2>
          <p className="text-[#64748B] mb-8 text-sm">NCERT textbook chapters. Score 75% or more on a chapter test to unlock the next chapter.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div onClick={() => navigate('/chapters/mathematics')} data-testid="chapters-math-btn"
              className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl p-7 text-white cursor-pointer hover:scale-[1.02] transition-all shadow-lg">
              <BookOpen className="w-7 h-7 mb-4 opacity-90" />
              <h3 className="text-xl font-bold">Ganita Prakash</h3>
              <p className="text-white/80 text-sm mt-1">Mathematics</p>
            </div>
            <div onClick={() => navigate('/chapters/science')} data-testid="chapters-science-btn"
              className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-7 text-white cursor-pointer hover:scale-[1.02] transition-all shadow-lg">
              <Microscope className="w-7 h-7 mb-4 opacity-90" />
              <h3 className="text-xl font-bold">Curiosity</h3>
              <p className="text-white/80 text-sm mt-1">Science</p>
            </div>
            <div onClick={() => navigate('/chapters/english')} data-testid="chapters-english-btn"
              className="bg-gradient-to-br from-purple-500 to-violet-600 rounded-2xl p-7 text-white cursor-pointer hover:scale-[1.02] transition-all shadow-lg">
              <Languages className="w-7 h-7 mb-4 opacity-90" />
              <h3 className="text-xl font-bold">English Literature</h3>
              <p className="text-white/80 text-sm mt-1">Honeysuckle, Honeycomb, Honeydew</p>
            </div>
          </div>
        </div>

        {/* Subject Performance Stats */}
        <div className="bg-white rounded-3xl p-8 border-2 border-slate-100 shadow-xl mb-8">
          <h2 className="text-3xl font-black text-[#1E293B] mb-6 flex items-center gap-3">
            <TrendingUp className="w-8 h-8 text-[#4D96FF]" />
            Your Subject Performance
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {subjectStats.map((stat, idx) => (
              <SubjectCard key={idx} stat={stat} />
            ))}
          </div>
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div
            onClick={() => navigate('/leaderboard')}
            data-testid="leaderboard-link"
            className="bg-white rounded-3xl p-6 border-2 border-slate-100 hover:-translate-y-2 transition-all cursor-pointer hover:shadow-xl"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-[#FFC03D] rounded-2xl flex items-center justify-center">
                  <Trophy className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#1E293B]">Leaderboard</h3>
                  <p className="text-sm text-[#64748B]">See how you rank</p>
                </div>
              </div>
              <ChevronRight className="w-6 h-6 text-[#64748B]" />
            </div>
          </div>

          <div
            onClick={() => navigate('/progress')}
            data-testid="progress-link"
            className="bg-white rounded-3xl p-6 border-2 border-slate-100 hover:-translate-y-2 transition-all cursor-pointer hover:shadow-xl"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-[#4D96FF] rounded-2xl flex items-center justify-center">
                  <TrendingUp className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#1E293B]">Progress Report</h3>
                  <p className="text-sm text-[#64748B]">View detailed analytics</p>
                </div>
              </div>
              <ChevronRight className="w-6 h-6 text-[#64748B]" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ icon, label, value, bgColor, iconColor, trend }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} whileHover={{ y: -8, boxShadow: '0 20px 40px rgba(0,0,0,0.1)' }}
    transition={{ duration: 0.3 }} data-testid={`stat-card-${label.toLowerCase().replace(/ /g, '-')}`}
    className="bg-white rounded-3xl p-6 border-2 border-slate-100 shadow-lg cursor-default">
    <div className="flex items-start justify-between mb-3">
      <motion.div whileHover={{ rotate: [0, -10, 10, 0], scale: 1.1 }} transition={{ duration: 0.4 }}
        className={`w-14 h-14 ${bgColor} rounded-2xl flex items-center justify-center ${iconColor}`}>
        {icon}
      </motion.div>
      {trend && (
        <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.5, type: 'spring' }}
          className="text-xs font-bold text-[#38E54D] bg-green-100 px-2 py-1 rounded-lg">{trend}</motion.span>
      )}
    </div>
    <p className="text-sm font-semibold text-[#64748B] mb-1">{label}</p>
    <p className="text-3xl font-black text-[#1E293B]">{value}</p>
  </motion.div>
);

const ActionCard = ({ title, description, icon, bgGradient, onClick, testId }) => (
  <motion.div data-testid={testId} onClick={onClick} whileHover={{ scale: 1.05, y: -5 }} whileTap={{ scale: 0.98 }}
    className={`bg-gradient-to-br ${bgGradient} rounded-3xl p-6 text-white cursor-pointer shadow-xl border-b-4 border-black/20 active:border-b-0 active:translate-y-1 relative overflow-hidden`}>
    <div className="absolute -top-6 -right-6 w-24 h-24 bg-white/10 rounded-full"></div>
    <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-white/5 rounded-full"></div>
    <motion.div className="mb-4 relative z-10" animate={{ y: [0, -3, 0] }} transition={{ repeat: Infinity, duration: 3, delay: Math.random() * 2 }}>{icon}</motion.div>
    <h3 className="text-2xl font-bold mb-2 relative z-10">{title}</h3>
    <p className="text-white/90 font-medium relative z-10">{description}</p>
  </motion.div>
);

const SubjectCard = ({ stat }) => {
  const accuracy = stat.questions_answered > 0 
    ? ((stat.correct_answers / stat.questions_answered) * 100).toFixed(1)
    : 0;

  const getSubjectColor = (subject) => {
    const colors = {
      mathematics: 'bg-blue-100 text-blue-600',
      science: 'bg-green-100 text-green-600',
      english: 'bg-purple-100 text-purple-600'
    };
    return colors[subject] || 'bg-gray-100 text-gray-600';
  };

  return (
    <div className="p-6 bg-slate-50 rounded-2xl border-2 border-slate-200">
      <div className={`inline-block px-4 py-2 rounded-xl font-bold text-sm mb-4 ${getSubjectColor(stat.subject)}`}>
        {stat.subject.charAt(0).toUpperCase() + stat.subject.slice(1)}
      </div>
      <div className="space-y-3">
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-[#64748B] font-semibold">Accuracy</span>
            <span className="font-bold text-[#1E293B]">{accuracy}%</span>
          </div>
          <div className="w-full bg-slate-200 rounded-full h-3">
            <div 
              className="bg-gradient-to-r from-[#4D96FF] to-[#A493FF] h-3 rounded-full progress-bar-animate"
              style={{ width: `${accuracy}%` }}
            ></div>
          </div>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-[#64748B]">Questions</span>
          <span className="font-bold text-[#1E293B]">{stat.questions_answered}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-[#64748B]">Elo Rating</span>
          <span className="font-bold text-[#1E293B]">{Math.round(stat.elo_rating)}</span>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
